import { useEffect, useId, useRef, type FormEvent, type ReactNode } from "react"
import { X } from "lucide-react"
import { cn } from "@/lib/utils"

interface DialogProps {
  open: boolean
  onClose: () => void
  title: ReactNode
  description?: ReactNode
  children: ReactNode
  /** Botões do rodapé. Com onSubmit, ficam dentro do form (type="submit" funciona). */
  footer?: ReactNode
  onSubmit?: (e: FormEvent<HTMLFormElement>) => void
  size?: "sm" | "md" | "lg" | "xl"
  /** Bloqueia fechar pelo fundo e pelo Esc enquanto algo é salvo. */
  busy?: boolean
  /** false: sem botão de fechar, Esc nem clique no fundo. Para decisões que não podem ser adiadas. */
  dismissible?: boolean
}

/**
 * Janela modal padrão. Estrutura estável para os testes E2E: o fundo é um div.fixed, o título é h2
 * e o primeiro botão é o de fechar.
 */
export function Dialog({ open, onClose, title, description, children, footer, onSubmit, size = "md", busy = false, dismissible = true }: DialogProps) {
  const titleId = useId()
  const descId = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef(onClose)
  const busyRef = useRef(busy || !dismissible)

  useEffect(() => { closeRef.current = onClose; busyRef.current = busy || !dismissible })

  useEffect(() => {
    if (!open) return
    const previous = document.activeElement as HTMLElement | null
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busyRef.current) { e.stopPropagation(); closeRef.current() }
    }
    window.addEventListener("keydown", onKey)
    const overflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    // Foca o primeiro campo; sem campos, o próprio painel
    const timer = setTimeout(() => {
      // Primeiro campo visível; arquivos e campos escondidos não recebem foco
      const candidates = panelRef.current?.querySelectorAll<HTMLElement>("[data-dialog-body] :is(input, select, textarea):not([type=hidden]):not([disabled]):not([tabindex='-1'])")
      const first = [...(candidates ?? [])].find((el) => el.offsetParent !== null)
      ;(first ?? panelRef.current)?.focus()
    }, 30)
    return () => {
      clearTimeout(timer)
      window.removeEventListener("keydown", onKey)
      document.body.style.overflow = overflow
      previous?.focus?.()
    }
  }, [open])

  if (!open) return null

  const width = { sm: "sm:max-w-sm", md: "sm:max-w-lg", lg: "sm:max-w-2xl", xl: "sm:max-w-4xl" }[size]

  const body = (
    <>
      <div data-dialog-body className="px-5 md:px-6 py-5 overflow-y-auto flex-1">{children}</div>
      {footer && (
        <div className="px-5 md:px-6 py-3.5 border-t bg-background/40 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 shrink-0">
          {footer}
        </div>
      )}
    </>
  )

  return (
    <div
      className="fixed inset-0 !m-0 z-50 flex items-end sm:items-center justify-center bg-neutral-950/40 sm:p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy && dismissible) onClose() }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className={cn("bg-card w-full border shadow-overlay rounded-t-xl sm:rounded-lg max-h-[92vh] flex flex-col focus:outline-none", width)}
      >
        <div className="flex items-start justify-between gap-4 px-5 md:px-6 py-4 border-b shrink-0">
          <div className="min-w-0">
            <h2 id={titleId} className="font-semibold text-[16px] tracking-[-0.01em]">{title}</h2>
            {description && <p id={descId} className="text-[13px] text-muted-foreground mt-0.5">{description}</p>}
          </div>
          {dismissible && (
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              aria-label="Fechar"
              className="h-8 w-8 -mr-2 -mt-1 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-inset shrink-0"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {onSubmit ? (
          <form onSubmit={onSubmit} className="flex flex-col min-h-0 flex-1" noValidate={false}>{body}</form>
        ) : body}
      </div>
    </div>
  )
}
